import { Stack } from "@mui/material";
import * as _ from "lodash";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dayjs } from "dayjs";
import { getBlockedDates } from "../../../../api/projects";
import { FormHelperTextError } from "../../../../components/form/errors";
import { SlotSelectionModal } from "../../../../components/invitations";
import { getFromAndToDateTimeRange } from "../../../../services/utils/date-and-time.utils";
import { useAppDispatch } from "../../../../store";
import { AmbassadorManualSlotsSection } from "../AmbassadorManualSlotsSection";
import {
  SlotModalState,
  VendorSlot,
} from "../../vendor-slot-selection/types";
import { validateAmbassadorSlots } from "./utils";

type MeetingRescheduleSlotsStepProps = {
  clientTimezoneValue?: string;
  showErrors: boolean;
  onValidityChange: (isValid: boolean) => void;
  onSlotsChange?: (slots: VendorSlot[]) => void;
};

export function MeetingRescheduleSlotsStep({
  clientTimezoneValue,
  showErrors,
  onValidityChange,
  onSlotsChange,
}: MeetingRescheduleSlotsStepProps) {
  const dispatch = useAppDispatch();
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [customSlotState, setCustomSlotState] = useState<{ slots: VendorSlot[] }>(
    { slots: [] }
  );
  const [customSlotModalState, setCustomSlotModalState] =
    useState<SlotModalState>(null);
  const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);
  const [manualFormError, setManualFormError] = useState("");
  const [blockedDates, setBlockedDates] = useState<string[]>([]);

  const clientTimezoneAbbr = useMemo(() => {
    return clientTimezoneValue
      ? moment().tz(clientTimezoneValue).format("z")
      : undefined;
  }, [clientTimezoneValue]);

  const browserTimezoneAbbr = useMemo(
    () => moment.tz(new Date(), browserTimezone).format("z"),
    [browserTimezone]
  );

  const fetchBlockedDatesForMonth = useCallback(
    async (monthDate: Date) => {
      const monthMoment = moment(monthDate);
      const from = monthMoment.startOf("month").format("YYYY-MM-DD");
      const to = monthMoment.endOf("month").format("YYYY-MM-DD");

      const response = await dispatch(getBlockedDates({ from, to }));

      if (response?.success && response?.data) {
        setBlockedDates(response.data);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    fetchBlockedDatesForMonth(new Date());
  }, [fetchBlockedDatesForMonth]);

  useEffect(() => {
    const validation = validateAmbassadorSlots(customSlotState);
    onValidityChange(validation.isFormValid);
    onSlotsChange?.(customSlotState.slots);
  }, [customSlotState, onValidityChange, onSlotsChange]);

  const handleSlotModalCalendarMonthChange = useCallback(
    (monthStart: Dayjs) => {
      fetchBlockedDatesForMonth(monthStart.toDate());
    },
    [fetchBlockedDatesForMonth]
  );

  const buildCustomSlot = (
    from: Date,
    to: Date,
    isTimeRange: boolean
  ): VendorSlot => ({
    selectedSlotTimeFrom: from,
    selectedSlotTimeTo: to,
    isTimeRange,
    error: "",
    convertedSlotTimeFrom: from?.toISOString(),
    convertedSlotTimeTo: to?.toISOString(),
  });

  const applySlotValidation = (nextState: { slots: VendorSlot[] }) => {
    if (showErrors) {
      const validation = validateAmbassadorSlots(nextState);
      setCustomSlotState(validation.updatedList);
      setManualFormError(validation.isFormValid ? "" : validation.formMessage);
      return;
    }

    setCustomSlotState(nextState);
    setManualFormError("");
  };

  const commitCustomSlot = (slot: VendorSlot, modalState: SlotModalState) => {
    const listClone = _.cloneDeep(customSlotState);

    if (modalState?.mode === "edit" && modalState.index !== null) {
      listClone.slots[modalState.index] = slot;
    } else {
      listClone.slots.push(slot);
    }

    applySlotValidation(listClone);
  };

  const commitCustomSlots = (slots: VendorSlot[]) => {
    if (slots.length === 0) return;

    const listClone = _.cloneDeep(customSlotState);
    listClone.slots = [...(listClone.slots || []), ...slots];
    applySlotValidation(listClone);
  };

  const handleCustomSlotAdd = () => {
    setIsUpdatingAvailability(customSlotState.slots.length > 0);
    setCustomSlotModalState({ mode: "create", index: null });
  };

  const handleCustomSlotRemove = (index: number) => {
    const listClone = _.cloneDeep(customSlotState);
    listClone.slots.splice(index, 1);
    applySlotValidation(listClone);
  };

  const handleCustomSlotSubmit = (data: unknown) => {
    if (!customSlotModalState) return;

    const payloads = Array.isArray(data) ? data : [data];
    const slots: VendorSlot[] = [];

    for (const payload of payloads) {
      const { dateFrom, dateTo } = getFromAndToDateTimeRange(payload);
      slots.push(
        buildCustomSlot(
          dateFrom,
          dateTo,
          (payload as { type?: string })?.type === "datetimerange"
        )
      );
    }

    if (isUpdatingAvailability) {
      applySlotValidation({ slots });
      setCustomSlotModalState(null);
      setIsUpdatingAvailability(false);
      return;
    }

    if (slots.length === 1) {
      commitCustomSlot(slots[0], customSlotModalState);
    } else {
      commitCustomSlots(slots);
    }

    setCustomSlotModalState(null);
    setIsUpdatingAvailability(false);
  };

  return (
    <>
      <Stack gap={2}>
        {manualFormError && (
          <FormHelperTextError showIcon error={manualFormError} />
        )}
        <AmbassadorManualSlotsSection
          slots={customSlotState.slots}
          browserTimezone={browserTimezone}
          browserTimezoneAbbr={browserTimezoneAbbr}
          clientTimezoneAbbr={clientTimezoneAbbr}
          clientTimezoneValue={clientTimezoneValue}
          onAddSlot={handleCustomSlotAdd}
          onEditSlot={() => undefined}
          onRemoveSlot={handleCustomSlotRemove}
          showErrors={showErrors}
        />
      </Stack>

      {customSlotModalState && (
        <SlotSelectionModal
          onClose={() => {
            setCustomSlotModalState(null);
            setIsUpdatingAvailability(false);
          }}
          onSubmit={handleCustomSlotSubmit}
          blockedDates={blockedDates}
          onCalendarMonthChange={handleSlotModalCalendarMonthChange}
          initialSelectedTimes={customSlotState.slots
            .map((slot) => slot.selectedSlotTimeFrom)
            .filter(Boolean)}
          slotIndex={
            customSlotModalState.mode === "edit" &&
            customSlotModalState.index !== null
              ? customSlotModalState.index + 1
              : customSlotState.slots.length + 1
          }
          slotDetails={
            customSlotModalState.mode === "edit" &&
            customSlotModalState.index !== null
              ? customSlotState.slots[customSlotModalState.index]
              : null
          }
          oppositeUserTimezone={clientTimezoneValue}
          userRole="vendor"
          allowMultiSelect={
            customSlotModalState.mode === "create" &&
            customSlotModalState.index === null
          }
          totalSlots={customSlotState.slots.length}
        />
      )}
    </>
  );
}
